import React from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, DEMO_MILESTONES } from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

export default function ScheduleCriticalPath() {
  const { theme } = useTheme();

  const onTrack   = DEMO_MILESTONES.filter(m => m.status === "on_track").length;
  const atRisk    = DEMO_MILESTONES.filter(m => m.status === "at_risk").length;
  const delayed   = DEMO_MILESTONES.filter(m => m.status === "delayed").length;
  const avgSlip   = Math.round(DEMO_MILESTONES.filter(m => m.slipDays > 0).reduce((s, m) => s + m.slipDays, 0) / Math.max(DEMO_MILESTONES.filter(m => m.slipDays > 0).length, 1));

  // Simplified Gantt data
  const ganttData = DEMO_PROJECTS.map(p => {
    const startDate = new Date(p.start);
    const endDate   = new Date(p.end);
    const today     = new Date("2026-05-11");
    const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    const elapsed   = Math.max(0, (today - startDate) / (1000 * 60 * 60 * 24));
    const remaining = Math.max(0, (endDate - today) / (1000 * 60 * 60 * 24));
    return {
      name: p.id,
      fullName: p.name,
      elapsed: Math.round(elapsed / totalDays * 100),
      remaining: Math.round(remaining / totalDays * 100),
      complete: p.pct,
      rag: p.rag,
      spi: p.spi,
    };
  });

  // SPI trend per project
  const spiData = DEMO_PROJECTS.map(p => ({ name: p.id, spi: p.spi, target: 1.0 }));

  // Float analysis (simulated)
  const floatData = DEMO_PROJECTS.map(p => ({
    name: p.id,
    float: Math.round((p.spi - 0.85) * 120),
    critical: p.spi < 0.9,
  }));

  return (
    <PageLayout
      title="Schedule & Critical Path"
      subtitle="Critical path visualization, milestone prediction, baseline vs actual analysis — P-07"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Milestones On Track" value={onTrack}   icon="✓" color={theme.success} sub="Meeting dates" />
        <KPICard label="Milestones At Risk"  value={atRisk}    icon="⚠" color={theme.warning} sub="Early warning" />
        <KPICard label="Milestones Delayed"  value={delayed}   icon="✗" color={theme.danger}  sub="Past due date" />
        <KPICard label="Avg Schedule Slip"   value={`${avgSlip}d`} icon="◈" color={theme.warning} sub="Days behind" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* SPI Bar */}
        <div className="pmo-card">
          <div className="section-title">Schedule Performance Index — All Projects</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={spiData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="name" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis domain={[0.7, 1.2]} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Bar dataKey="spi" name="SPI" radius={[4,4,0,0]}>
                {spiData.map((d, i) => <Bar key={i} fill={d.spi >= 1 ? theme.success : d.spi >= 0.9 ? theme.warning : theme.danger} />)}
                {spiData.map((d, i) => (
                  <React.Fragment key={i}>
                    <rect x={0} y={0} width={0} height={0} fill={d.spi >= 1 ? theme.success : d.spi >= 0.9 ? theme.warning : theme.danger} />
                  </React.Fragment>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Float Analysis */}
        <div className="pmo-card">
          <div className="section-title">Schedule Float Analysis (Days)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={floatData} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}44`} horizontal={false} />
              <XAxis type="number" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: theme.textMuted, fontSize: 10 }} width={35} />
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Bar dataKey="float" name="Float (days)" radius={[0,4,4,0]}>
                {floatData.map((d, i) => (
                  <Bar key={i} fill={d.float <= 0 ? theme.danger : d.float < 15 ? theme.warning : theme.success} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Simplified Gantt */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Portfolio Gantt — % Elapsed vs % Complete</div>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Project</th><th>Start</th><th>End</th><th>Timeline Progress</th><th>% Complete</th><th>SPI</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_PROJECTS.map((p, i) => {
                const g = ganttData[i];
                return (
                  <tr key={p.id}>
                    <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{p.id}</td>
                    <td style={{ color: theme.textPrimary, fontWeight: 500, maxWidth: "200px" }}>{p.name}</td>
                    <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{p.start}</td>
                    <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{p.end}</td>
                    <td style={{ minWidth: "200px" }}>
                      <div style={{ height: "14px", background: theme.cardBorder, borderRadius: "3px", position: "relative", overflow: "hidden" }}>
                        <div style={{ width: `${g.elapsed}%`, height: "100%", background: `${theme.textMuted}55`, position: "absolute", left: 0, top: 0 }} />
                        <div style={{ width: `${g.complete}%`, height: "100%", background: g.rag === "red" ? theme.danger : g.rag === "amber" ? theme.warning : theme.success, position: "absolute", left: 0, top: 0, opacity: 0.8, borderRadius: "3px" }} />
                      </div>
                      <div style={{ fontSize: "9px", color: theme.textMuted, marginTop: "2px", display: "flex", justifyContent: "space-between" }}>
                        <span>Elapsed: {g.elapsed}%</span>
                        <span>Done: {g.complete}%</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>{p.pct}%</td>
                    <td style={{ color: p.spi >= 1 ? theme.success : p.spi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace", fontWeight: 700 }}>{p.spi.toFixed(2)}</td>
                    <td><RAGBadge status={p.rag} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Milestone Table */}
      <div className="pmo-card">
        <div className="section-title">Milestone Tracker</div>
        <table>
          <thead>
            <tr>{["Project","Milestone","Due Date","Slip (Days)","ML Prediction","Status"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_MILESTONES.map((m, i) => (
              <tr key={i} className={m.status === "delayed" ? "danger-row" : m.status === "at_risk" ? "warning-row" : ""}>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{m.project}</td>
                <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{m.name}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{m.due}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", color: m.slipDays > 30 ? theme.danger : m.slipDays > 0 ? theme.warning : theme.success }}>{m.slipDays > 0 ? `+${m.slipDays}d` : "—"}</td>
                <td style={{ fontSize: "11px", color: theme.textSecondary }}>
                  {m.slipDays > 30 ? "High slip probability — escalation recommended"
                    : m.slipDays > 0 ? "Moderate delay risk — monitor weekly"
                    : "On schedule — maintain current trajectory"}
                </td>
                <td>
                  <RAGBadge
                    status={m.status === "on_track" ? "green" : m.status === "at_risk" ? "amber" : "red"}
                    label={m.status === "on_track" ? "ON TRACK" : m.status === "at_risk" ? "AT RISK" : "DELAYED"}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
